import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useKanban } from './KanbanContext';
import { useAuth } from './AuthContext';
import { LABS_API_URL, DEFAULT_HEADERS, getEmailDomainMark } from '../utils/config';
import { fetchUsersByBoardCode, fetchAdminUsers } from '../utils/api/permissionsApi';

function AssigneeFilter() {
  const { activeWorkspace, workspaces, updateAssigneeFilter, workspaceData } = useKanban();
  const { userData, currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersWithBoardAccess, setUsersWithBoardAccess] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);
  
  // Estado para el nuevo dropdown con autocompletado
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [displaySuggestions, setDisplaySuggestions] = useState([]);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Obtener el filtro activo actual
  const activeFilter = workspaces[activeWorkspace]?.activeAssigneeFilter || '';

  // Función para filtrar usuarios por código del workspace
  const filterUsersByWorkspaceCode = useCallback((users, workspaceCode) => {
    if (!workspaceCode) {
      setFilteredUsers(users);
      return;
    }
    
    // Filtrar usuarios que pertenezcan al workspace (cuyo código de departamento coincida con el código del workspace)
    const filtered = users.filter(user => {
      if (!user.department) return false;
      
      // El campo department puede tener diferentes formatos:
      // 1. Solo el código: "14337"
      // 2. Formato completo: "14337 - ADM - GGL - XLABS IA"
      
      // Primero intentar comparación directa (caso 1)
      if (user.department === workspaceCode || user.department?.toString() === workspaceCode?.toString()) {
        return true;
      }
      
      // Si no coincide directamente, extraer el código del formato completo (caso 2)
      const userDeptMatch = user.department.match(/^(\d+)/);
      const userDeptCode = userDeptMatch ? userDeptMatch[1] : '';
      return userDeptCode === workspaceCode;
    });
    
    setFilteredUsers(filtered);
  }, []);

  // Función para obtener el código del workspace activo
  const getActiveWorkspaceCode = useCallback(() => {
    if (!activeWorkspace || !workspaceData) return null;
    
    // Buscar el workspace activo en workspaceData
    const currentWorkspace = workspaceData.find(ws => ws.id === activeWorkspace);
    
    // Verificar que el código existe y no está vacío
    if (currentWorkspace && currentWorkspace.code && currentWorkspace.code.trim() !== '') {
      return currentWorkspace.code;
    }
    
    // Si no encontramos el código, intentar extraerlo del departamento del usuario como fallback
    if (userData && userData.body && userData.body.department) {
      const departmentMatch = userData.body.department.match(/^(\d+)/);
      return departmentMatch ? departmentMatch[1] : null;
    }
    
    return null;
  }, [activeWorkspace, workspaceData, userData]);

  // Formatear nombre de usuario desde email
  const formatUserName = (email) => {
    if (!email) return '';
    
    // Si no es un correo electrónico (no contiene @), devolver como está
    if (!email.includes('@')) return email;
    
    // Obtener la parte antes del @
    const namePart = email.split('@')[0];
    
    // Reemplazar puntos por espacios
    const nameWithSpaces = namePart.replace(/\./g, ' ');
    
    // Dividir el nombre completo en partes
    const nameParts = nameWithSpaces.split(' ');
    
    // Extraer el primer nombre y el primer apellido
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[1] : '';
    
    // Capitalizar y combinar nombre y apellido
    const formattedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    const formattedLastName = lastName ? ' ' + lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
    
    const mark = getEmailDomainMark(email);
    return formattedFirstName + formattedLastName + (mark ? ` ${mark}` : '');
  };

  // Inicializar el valor de búsqueda cuando cambia el filtro activo
  useEffect(() => {
    if (activeFilter) {
      // Encontrar el usuario que corresponde al email activo
      const user = filteredUsers.find(u => u.email === activeFilter);
      if (user) {
        setSearchQuery(formatUserName(user.email));
      } else {
        setSearchQuery(formatUserName(activeFilter));
      }
    } else {
      setSearchQuery('');
    }
  }, [activeFilter, filteredUsers]);

  useEffect(() => {
    // Función para obtener todos los usuarios
    const fetchAllUsers = async () => {
      // Verificar si ya tenemos datos en localStorage
      const cachedUsers = localStorage.getItem('allUsersData');
      const cachedTimestamp = localStorage.getItem('allUsersTimestamp');
      const currentTime = new Date().getTime();
      
      // Usar caché si existe y tiene menos de 24 horas
      if (cachedUsers && cachedTimestamp && 
          (currentTime - parseInt(cachedTimestamp)) < 24 * 60 * 60 * 1000) {
        try {
          const users = JSON.parse(cachedUsers);
          
          setAllUsers(users);
          
          // Filtrar usuarios por código del workspace activo
          const workspaceCode = getActiveWorkspaceCode();
          if (workspaceCode) {
            filterUsersByWorkspaceCode(users, workspaceCode);
          } else {
            // Si no hay código del workspace, mostrar todos los usuarios
            setFilteredUsers(users);
          }
          
          setUsersLoaded(true);
          return;
        } catch (error) {
          // Si hay error en la caché, continuamos con la petición a la API
        }
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${LABS_API_URL}/workspace/all-users`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error(`Error al obtener usuarios: ${response.status}`);
        }
        
        const responseData = await response.json();
        
        // Manejar el formato de respuesta anidado
        let users = [];
        
        if (responseData && responseData.body) {
          try {
            // La respuesta tiene el body como string JSON, debemos parsearlo
            users = JSON.parse(responseData.body);
            
            if (Array.isArray(users)) {
              setAllUsers(users);
              
              // Filtrar usuarios por código del workspace activo
              const workspaceCode = getActiveWorkspaceCode();
              if (workspaceCode) {
                filterUsersByWorkspaceCode(users, workspaceCode);
              } else {
                // Si no hay código del workspace, mostrar todos los usuarios
                setFilteredUsers(users);
              }
              
              // Guardar todos los usuarios en localStorage (no filtrados)
              localStorage.setItem('allUsersData', JSON.stringify(users));
              localStorage.setItem('allUsersTimestamp', currentTime.toString());
            } else {
              setFilteredUsers([]);
            }
          } catch (parseError) {
            setError('Error al procesar los datos de usuarios');
            setFilteredUsers([]);
          }
        } else {
          setFilteredUsers([]);
        }
      } catch (err) {
        setError(err.message);
        setFilteredUsers([]);
      } finally {
        setLoading(false);
        setUsersLoaded(true);
      }
    };
    
    if (currentUser) {
      fetchAllUsers();
    }
  }, [currentUser, getActiveWorkspaceCode, filterUsersByWorkspaceCode]);
  
  // Filtrar usuarios por código del workspace cuando cambia el workspace activo
  useEffect(() => {
    if (allUsers.length > 0) {
      const workspaceCode = getActiveWorkspaceCode();
      if (workspaceCode) {
        filterUsersByWorkspaceCode(allUsers, workspaceCode);
      } else {
        // Si no hay código del workspace, mostrar todos los usuarios
        setFilteredUsers(allUsers);
      }
    }
  }, [activeWorkspace, workspaceData, allUsers, getActiveWorkspaceCode, filterUsersByWorkspaceCode]);
  
  // Obtener usuarios con acceso especial al tablero (por additionalCodes)
  useEffect(() => {
    const fetchUsersWithBoardAccess = async () => {
      const workspaceCode = getActiveWorkspaceCode();
      if (!workspaceCode) {
        // Si no hay código, no hay usuarios con acceso especial, pero no es un error
        setUsersWithBoardAccess([]);
        return;
      }

      try {
        const usersWithAccess = await fetchUsersByBoardCode(workspaceCode);
        setUsersWithBoardAccess(usersWithAccess);
      } catch (error) {
        // En caso de error, simplemente no mostrar usuarios con acceso especial
        setUsersWithBoardAccess([]);
      }
    };

    if (usersLoaded) {
      fetchUsersWithBoardAccess();
    }
  }, [activeWorkspace, workspaceData, usersLoaded, getActiveWorkspaceCode]);

  // Obtener usuarios administradores
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const adminUserEmails = await fetchAdminUsers();
        setAdminUsers(adminUserEmails);
      } catch (error) {
        console.error('Error fetching admin users:', error);
        // En caso de error, simplemente no mostrar administradores
        setAdminUsers([]);
      }
    };

    if (usersLoaded) {
      fetchAdmins();
    }
  }, [usersLoaded]);
  
  // Effect para cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Filtrar usuarios basado en la búsqueda
  useEffect(() => {
    // Crear una lista combinada de usuarios únicos
    // Primero obtener todos los emails únicos
    // eslint-disable-next-line no-unused-vars
    const allUserEmails = new Set([
      ...filteredUsers.map(u => u.email),
      ...usersWithBoardAccess,
      ...adminUsers
    ]);
    
    // Crear objetos de usuario para los administradores que no están en filteredUsers
    const adminUsersList = adminUsers
      .filter(email => !filteredUsers.some(u => u.email?.toLowerCase() === email?.toLowerCase()))
      .map(email => {
        // Buscar el usuario completo en allUsers si existe (comparación case-insensitive)
        const fullUser = allUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
        const hasAccess = usersWithBoardAccess.some(u => u?.toLowerCase() === email?.toLowerCase());
        if (fullUser) {
          return {
            ...fullUser,
            isAdmin: true,
            hasSpecialAccess: hasAccess
          };
        }
        // Si no está en allUsers, crear un objeto básico
        return {
          email: email,
          department: 'Administrador', // Indicador visual
          isAdmin: true,
          hasSpecialAccess: hasAccess
        };
      });
    
    // Crear objetos de usuario para los que tienen acceso especial pero no están en filteredUsers ni son admin
    const additionalUsers = usersWithBoardAccess
      .filter(email => !filteredUsers.some(u => u.email === email) && !adminUsers.includes(email))
      .map(email => ({
        email: email,
        department: 'Acceso especial', // Indicador visual
        hasSpecialAccess: true,
        isAdmin: false
      }));
    
    // Combinar usuarios regulares con usuarios con acceso especial y administradores
    const combinedUsers = [
      ...filteredUsers.map(user => ({
        ...user,
        hasSpecialAccess: usersWithBoardAccess.some(u => u?.toLowerCase() === user.email?.toLowerCase()),
        isAdmin: adminUsers.some(a => a?.toLowerCase() === user.email?.toLowerCase())
      })),
      ...adminUsersList,
      ...additionalUsers
    ];
    
    // Ordenar: administradores primero, luego el resto
    combinedUsers.sort((a, b) => {
      if (a.isAdmin && !b.isAdmin) return -1;
      if (!a.isAdmin && b.isAdmin) return 1;
      return 0;
    });
    
    if (searchQuery.trim() === '') {
      // Si está vacío, mostrar todos los usuarios combinados
      setDisplaySuggestions(combinedUsers);
    } else {
      // Filtrar usuarios que coincidan con la búsqueda
      const filtered = combinedUsers.filter(user => {
        const userName = formatUserName(user.email).toLowerCase();
        return userName.includes(searchQuery.toLowerCase());
      });
      setDisplaySuggestions(filtered);
    }
  }, [searchQuery, filteredUsers, usersWithBoardAccess, adminUsers, allUsers]);
  
  // Manejar el cambio en el input de búsqueda
  const handleFilterChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setShowSuggestions(true);
    
    // Si el campo está vacío, limpiar el filtro
    if (newQuery === '') {
      updateAssigneeFilter(activeWorkspace, '');
    }
  };
  
  // Manejar la selección de un usuario
  const handleSelectUser = (user) => {
    setSearchQuery(formatUserName(user.email));
    updateAssigneeFilter(activeWorkspace, user.email);
    setShowSuggestions(false);
  };
  
  // Limpiar el filtro
  const clearFilter = () => {
    setSearchQuery('');
    updateAssigneeFilter(activeWorkspace, '');
  };
  

  
  // Si todavía no se han cargado los usuarios, mostrar un mensaje de carga
  if (!usersLoaded) {
    return (
      <div className="assignee-filter-container">
        <div className="loading-message">
          <div className="mini-spinner"></div>
          <span>Cargando usuarios...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="assignee-filter-container">
      <div className="filter-controls">
        <div className="autocomplete-container">
          <input
            type="text"
            className="tag-filter-input"
            placeholder="Filtrar por responsable..."
            value={searchQuery}
            onChange={handleFilterChange}
            onFocus={() => setShowSuggestions(true)}
            ref={searchInputRef}
          />
          
          {showSuggestions && displaySuggestions.length > 0 && (
            <>
              <div className="autocomplete-suggestions" ref={suggestionsRef}>
                {displaySuggestions.map((user, index) => {
                  const formattedName = formatUserName(user.email);
                  return (
                    <div 
                      key={index} 
                      className="suggestion-item"
                      onClick={() => handleSelectUser(user)}
                      title={`${formattedName} (${user.department || ''})`}
                    >
                      <span className="user-name">{formattedName}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mobile-overlay" onClick={() => setShowSuggestions(false)}></div>
            </>
          )}
        </div>
        
        {loading && <div className="mini-spinner"></div>}
        
        {/* Botón X con nuevo estilo */}
        {activeFilter && (
          <button 
            className="clear-filter-btn"
            onClick={clearFilter}
            title="Limpiar filtro"
          >
            ✕
          </button>
        )}
        

      </div>
      

      
      {/* Mostrar mensaje de error si existe */}
      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}
    </div>
  );
}

export default AssigneeFilter; 